import React, { useEffect, useState, useMemo } from "react";
import apiClient from '../utils/apiClient';
import Swal from "sweetalert2";
import { SERVER_BASE } from '../config/server';
import { useTable, usePagination, useGlobalFilter, useSortBy } from "react-table";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faEdit, faTrash, faPlus, faPencil } from "@fortawesome/free-solid-svg-icons";
import moment from 'moment';

const UOMList = ({ permissions }) => {
  const [uoms, setUoms] = useState([]); // UOM data
  const [unit, setUnit] = useState(""); // Unit input
  const [abbreviation, setAbbreviation] = useState(""); // Abbreviation input
  const [editId, setEditId] = useState(null); // ID of the UOM being 
  const code = localStorage.getItem('code');
  const token = localStorage.getItem("token")
  const role = localStorage.getItem('role');
  const email = localStorage.getItem('email');
  const [loadingData, setLoadingData] = useState(true);
  useEffect(() => {
    fetchUOMs();
  }, []);

  const fetchUOMs = async () => {
    try {
      setLoadingData(true)
      const response = await apiClient.get(SERVER_BASE + "/getUoms", {
        params: { code },
        headers: {
          Authorization: `Bearer ${token}`, // Add the JWT token in the header
        },
      });
      setUoms(response.data);
    } catch (error) {
      console.error("Error fetching UOMs:", error);
    } finally {
      setLoadingData(false)
    }
  };

  // Add or Update a UOM
  const handleSaveUOM = () => {
    if (!unit || !abbreviation) {
      Swal.fire("Error", "Both fields are required.", "error");
      return;
    }

    Swal.fire({
      title: editId ? "Confirm Update" : "Confirm Add",
      text: editId
        ? "Are you sure you want to update this UOM?"
        : "Are you sure you want to add this UOM?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    }).then((result) => {
      if (result.isConfirmed) {
        if (editId) {
          // Update UOM
          apiClient
            .put(`${SERVER_BASE}/updateUoms/${editId}`, { unit, abbreviation, email }, {
              headers: {
                Authorization: `Bearer ${token}`, // Add the JWT token in the header
              }
            })
            .then(() => {
              Swal.fire("Success", "UOM updated successfully.", "success");
              fetchUOMs();
              resetForm();
            })
            .catch((err) => {
              Swal.fire("Error", err.response?.data?.message || "An error occurred.", "error");
            });
        } else {
          // Add UOM
          apiClient
            .post(SERVER_BASE + "/addUoms", { unit, abbreviation, code, email }, {
              headers: {
                Authorization: `Bearer ${token}`, // Add the JWT token in the header
              }
            })
            .then((response) => {
              Swal.fire("Success", "UOM added successfully.", "success");
              fetchUOMs();
              resetForm();
            })
            .catch((err) => {
              Swal.fire("Error", err.response?.data?.message || "An error occurred.", "error");
            });
        }
      }
    });
  };


  // Delete UOM
  const handleDeleteUOM = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        apiClient
          .delete(`${SERVER_BASE}/deleteUoms/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`, // Add the JWT token in the header
            }
          })
          .then(() => {
            Swal.fire("Deleted!", "Your UOM has been deleted.", "success");
            fetchUOMs();
          })
          .catch((error) => {
            Swal.fire("Error", "An error occurred while deleting the UOM.", "error");
          });
      }
    });
  };

  // Edit UOM
  const handleEditUOM = (uom) => {
    setEditId(uom.id);
    setUnit(uom.unit);
    setAbbreviation(uom.abbreviation);
  };

  // Reset form
  const resetForm = () => {
    setEditId(null);
    setUnit("");
    setAbbreviation("");
  };

  // React-table setup
  const columns = useMemo(
    () => [
      {
        Header: 'S.No',
        Cell: ({ row }) => row.index + 1,
      },
      { Header: "Unit", accessor: "unit" },
      { Header: "Abbreviation", accessor: "abbreviation" },
      {
        Header: "#",
        accessor: "Action",
        Cell: ({ row }) => {
          const {
            createdAt,
            createdByName,
            updatedAt,
            updatedByName,
          } = row.original;
          return (
            <div className="dropdown">
              <button
                className="btn btn-sm btn-light"
                type="button"
                id={`dropdownMenuButton-${row.index}`}
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul
                className="dropdown-menu"
                aria-labelledby={`dropdownMenuButton-${row.index}`}
              >
                {(role === "itManager" || permissions?.edit) && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleEditUOM(row.original)}
                    >
                      <FontAwesomeIcon icon={faEdit} className="me-2" />
                      Edit
                    </button>
                  </li>
                )}

                {(role === "itManager" || permissions?.delete) && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleDeleteUOM(row.original.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      Delete
                    </button>
                  </li>
                )}
                {(createdByName || updatedByName) && (
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                )}
                {createdByName && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPlus} /> {createdByName}
                    {createdAt &&
                      ` (${moment(createdAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}

                {/* Updated Info */}
                {updatedByName && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPencil} /> {updatedByName}
                    {updatedAt &&
                      ` (${moment(updatedAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}
              </ul>
            </div>
          );
        },
      }
    ],
    [permissions]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data: uoms,
      initialState: { pageIndex: 0, pageSize: 20 }, // min-20 page size
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  if (loadingData) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-success" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-3 fs-5">Please Wait...</div>
      </div>
    );
  }
  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>UOM List</h3>
        <div className="ledger-count">
          Total UOMs: <strong>{uoms.length}</strong>
        </div>
      </div>
      {(role == 'itManager' || permissions?.write) && (
        <>
          {/* Add/Edit UOM Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault(); // Prevent default form submission
              handleSaveUOM();
            }}
            className="formcontainer mb-4"
          >
            <div className="row">
              {/* Unit Field */}
              <div className="col-md-3">
                <label htmlFor="unit" className="form-label">
                  Unit
                </label>
                <input
                  type="text"
                  id="unit"
                  className="form-control"
                  placeholder="Enter Unit"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                />
              </div>

              {/* Abbreviation Field */}
              <div className="col-md-3">
                <label htmlFor="abbreviation" className="form-label">
                  Abbreviation
                </label>
                <input
                  type="text"
                  id="abbreviation"
                  className="form-control"
                  placeholder="Enter Abbreviation"
                  value={abbreviation}
                  onChange={(e) => setAbbreviation(e.target.value)}
                />
              </div>

              {/* Action Buttons */}
              <div className="col-md-4 d-flex align-items-end">
                <button type="submit" className="btn btn-outline-success me-2">
                  {editId ? "Update UOM" : "Add UOM"}
                </button>
                {editId && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </form>
        </>
      )}
      {/* React Table */}
      {(role == 'itManager' || permissions?.read || permissions?.edit || permissions?.delete) && (
        <div className="formcontainer">
          <div className="d-flex justify-content-between gap-3">
            <input
              className="form-control"
              placeholder="Search"
              value={globalFilter || ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
            <label className="d-inline-flex align-items-center ">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="form-select w-auto"
              >
                {[20, 30, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    Show {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="table-responsive">
            <table className="table table-striped" {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                        {column.render("Header")}
                        {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-flex align-items-center mb-3">
            {/* Previous – left corner */}
            <button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="btn btn-secondary me-auto"
              style={{ minWidth: 110 }}   // keeps width consistent
            >
              Previous
            </button>

            {/* Center block for page index & size selector */}
            <div className="text-center flex-grow-1 d-flex flex-column align-items-center">
              {/* Row 1: Page index and size selector in one line */}
              <div className="d-flex align-items-center gap-3">
                <div className="fw-semibold">
                  Page {pageIndex + 1} of {pageOptions.length}
                </div>


              </div>
            </div>


            {/* Next – right corner */}
            <button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="btn btn-secondary ms-auto"
              style={{ minWidth: 110 }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UOMList;
